import { Fragment } from 'react';
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { CollapseProps, Divider, Flex, Tooltip, Typography } from 'antd';
import classNames from 'classnames';
import { copyToClipboard } from '@Utils/copyToClipboard';
import { ReactComponent as ChevroneDownIcon } from '@Icons/additional/chevron-down-blue.svg';
import { ReactComponent as CopyIcon } from '@Icons/additional/copy.svg';
import { MCollapse } from '@Components/Base';

import { LegalInfo } from '@Types/api/common/dictionary/LegalInfo';
import {
  prepareEmploymentInformation,
  prepareInsuranceDetails,
  preparePersonalInformation,
  prepareVisaPassportInformation,
} from './utils';
import usePrepareCollapseData from './hook/usePrepareCollapseData';
import {
  EmploymentInformation,
  InsuranceDetails,
  PersonalInformation,
  VisaPassportInformation,
} from './types';

import styles from './details.module.css';

const RenderIcon: CollapseProps['expandIcon'] = ({ isActive }) => (
  <ChevroneDownIcon
    className={styles.expandIcon}
    style={{ transform: `rotate(${isActive ? 180 : 0}deg)` }}
  />
);

type DetailsCardItem = {
  title: string;
  description: string;
  isCopy?: boolean;
};

const getDetailsCard = (
  title: string,
  items: DetailsCardItem[],
  isMobile: boolean
) => {
  const dividerEl = <Divider className={styles.cardLineDivider} />;

  return {
    id: title.toUpperCase(),
    label: title,
    children: items.map((item, index) => (
      <Fragment key={item.title}>
        {(isMobile && index === 0) || !isMobile ? dividerEl : null}
        <Flex className={styles.detailsCardItem}>
          <Typography.Text className={styles.detailsCardItemTitle}>
            {item.title}
          </Typography.Text>
          <Flex gap={5} align="center">
            <Typography.Text className={styles.detailsCardItemDescription}>
              {item.description}
            </Typography.Text>

            {item.isCopy && (
              <Tooltip
                placement="topLeft"
                title="Copied to Clipboard!"
                trigger="click"
              >
                <CopyIcon
                  key={item.title}
                  onClick={() => copyToClipboard(item.description)}
                  className={styles.copyIcon}
                />
              </Tooltip>
            )}
          </Flex>
        </Flex>
      </Fragment>
    )),
  };
};

interface DetailsCardsProps {
  employmentInformation: EmploymentInformation;
  insuranceDetails: InsuranceDetails;
  personalInformation: PersonalInformation;
  visaPassportInformation: VisaPassportInformation;
  isMobile: boolean;
}

const DetailsCards: React.FC<DetailsCardsProps> = ({
  employmentInformation,
  insuranceDetails,
  personalInformation,
  visaPassportInformation,
  isMobile,
}) => {
  const employmentInformationData = prepareEmploymentInformation(
    employmentInformation
  );
  const insuranceDetailsData = prepareInsuranceDetails(insuranceDetails);
  const personalInformationData =
    preparePersonalInformation(personalInformation);
  const visaPassportInformationData = prepareVisaPassportInformation(
    visaPassportInformation
  );

  const collapseItems = [
    getDetailsCard(
      'Personal Identification Information',
      personalInformationData,
      isMobile
    ),
    getDetailsCard(
      'Visa and Passport Information',
      visaPassportInformationData,
      isMobile
    ),
    getDetailsCard(
      'Employment Information',
      employmentInformationData,
      isMobile
    ),
    getDetailsCard('Insurance Details', insuranceDetailsData, isMobile),
  ];

  return (
    <MCollapse
      collapsible="icon"
      className={classNames(styles.detailsCards)}
      expandIcon={RenderIcon}
      items={collapseItems}
    />
  );
};

interface DetailsProps {
  legalInfoData: LegalInfo;
  isMobile: boolean;
}

const Details: React.FC<DetailsProps> = ({ legalInfoData, isMobile }) => {
  const {
    employmentInformation,
    insuranceDetails,
    personalInformation,
    visaPassportInformation,
  } = usePrepareCollapseData({ legalInfoData });

  return (
    <Flex vertical className={styles.wrapper}>
      <DetailsCards
        employmentInformation={employmentInformation}
        insuranceDetails={insuranceDetails}
        personalInformation={personalInformation}
        visaPassportInformation={visaPassportInformation}
        isMobile={isMobile}
      />
    </Flex>
  );
};

export default Details;
